<!--@format-->
<template>
    <div class="board-view-widget">
        <div class="widget-header">
            <h3>{{ view.id ? 'Update' : 'Add' }} View</h3>
            <div class="widget-header-actions">
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
        </div>
        <div class="content-wrapper">
            <template v-if="loading">
                <loading-screen-svg />
            </template>
            <template v-else>
                <app-input v-model="view.name" :label="'Name'" :placeholder="'My view'" />

                <app-border-section v-if="filterable" topText="Filters">
                    <e24-filter-outline-svg slot="icon" />
                    <board-view-filters
                        :type="view.type"
                        :headers="headers"
                        :view="view"
                        @update:filters="updateFilters"
                    />
                </app-border-section>

                <app-border-section topText="Hide/Show Columns">
                    <d24-show-hide-outline-svg slot="icon" />
                    <board-view-show-hide
                        :headers="headers"
                        :view="view"
                        @update:header="updateHeaders"
                    />
                </app-border-section>

                <app-border-section topText="Pinned Columns">
                    <f24-pin-outline-svg slot="icon" />
                    <board-view-pins
                        :headers="headers"
                        :view="view"
                        @update:header="updateHeaders"
                    />
                </app-border-section>

                <app-border-section topText="Choose an Icon">
                    <board-icon-select v-model="view.icon" />
                </app-border-section>
            </template>
        </div>
        <div class="widget-footer">
            <div class="cancel-container">
                <app-button class="cancel-btn btn normal" @click="closeWidget()">
                    Cancel
                </app-button>
            </div>
            <div class="action-container">
                <app-button
                    v-if="view.id"
                    class="btn table-actions-btn delete-btn"
                    :loading="loading"
                    @click="deleteView()"
                >
                    Delete
                </app-button>
                <app-button
                    class="btn table-actions-btn save-btn"
                    :loading="loading"
                    @click="saveView()"
                >
                    Save
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const AppInput = () => import('@/components/common/app-input');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const BoardIconSelect = () => import('@/components/sliding-widgets/boards/board-icon-select');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const AppBorderSection = () => import('@/components/common/app-border-section');
const BoardViewFilters = () => import('@/components/board/filters/board-view-filters');
const BoardViewPins = () => import('@/components/board/board-view-pins');
const BoardViewShowHide = () => import('@/components/board/board-view-show-hide');
const E24FilterOutlineSvg = () => import('@/components/svg/grid/e24-filter-outline-svg');
const F24PinOutlineSvg = () => import('@/components/svg/grid/f24-pin-outline-svg');
const D24ShowHideOutlineSvg = () => import('@/components/svg/grid/d24-show-hide-outline-svg');

import EventBus from '@/scripts/event-bus.js';
import AppPopup from '@/components/popups/app-popup';
import BoardUtility from '@/scripts/board-utility';
import { CustomFieldService } from '@/scripts/services/custom-fields/custom-field-service';

export default {
    name: 'board-view-widget',

    components: {
        'app-button': AppButton,
        'app-input': AppInput,
        'widget-close-svg': WidgetCloseSvg,
        'loading-screen-svg': LoadingScreenSvg,
        'app-border-section': AppBorderSection,
        'board-view-filters': BoardViewFilters,
        'board-view-pins': BoardViewPins,
        'board-view-show-hide': BoardViewShowHide,
        'board-icon-select': BoardIconSelect,
        'e24-filter-outline-svg': E24FilterOutlineSvg,
        'f24-pin-outline-svg': F24PinOutlineSvg,
        'd24-show-hide-outline-svg': D24ShowHideOutlineSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            loading: true,
            headers: [],
            filterable: this.widget.filterable,
            view: {
                id: this.widget.viewId,
                type: this.widget.type,
                userId: this.widget.userId,
                name: null,

                filters: {
                    operation: null,
                    value: [],
                },
                icon: null,
                sort: null,
                display: 'table',
                itemsPerPage: 10,
                headers: [],
            },
        };
    },

    created() {
        this.loading = true;

        if (this.view.id) {
            this.getViewById()
                .then(this.setStaticHeaders)
                .then(this.setCustomFieldHeaders)
                .then(() => {
                    this.view.headers = this.parseConfigHeaders(this.view.headers);
                    this.loading = false;
                });
        } else {
            this.setStaticHeaders()
                .then(this.setCustomFieldHeaders)
                .then(() => {
                    this.view.headers = BoardUtility.getDefaultViewHeaders(this.headers);
                    this.loading = false;
                });
        }
    },

    computed: {
        clientId() {
            return this.$store.state.context.client.id;
        },

        accountId() {
            return this.$store.state.context.account.id;
        },
    },

    methods: {
        setStaticHeaders() {
            this.headers = BoardUtility.getHeaderDefinitions(this.view.type);
            return Promise.resolve();
        },

        setCustomFieldHeaders() {
            var customFieldType = BoardUtility.getCustomFieldTypeByBoardType(this.view.type);
            if (!customFieldType) {
                return Promise.resolve();
            }

            return new Promise((resolve) => {
                CustomFieldService.getCustomFieldHeaders(
                    customFieldType,
                    this.clientId || this.accountId,
                )
                    .then((res) => {
                        const newHeaders = res.data.data;

                        newHeaders.forEach((header) => {
                            var customHeader = this.mapCustomHeader(header);
                            this.headers.push(customHeader);
                        });

                        resolve();
                    })
                    .catch((error) => {
                        if (error.response.status === 404) {
                            resolve();
                            return;
                        }
                    });
            });
        },

        mapCustomHeader(item) {
            var key = `${item.Id}`;

            return {
                key: key,
                type: CustomFieldService.mapCustomFieldHeaderType(item.InputType),
                label: item.Name,
                property: key,
                description: item.Description,
                sortable: false,
                sortProperty: null,
                editable: true,
                filterable: true,
            };
        },

        parseConfigHeaders(configHeaders) {
            var allowedConfigHeaders = configHeaders.filter((c) =>
                this.headers.some((h) => h.key === c.key),
            );

            for (var i = 0; i < this.headers.length; i++) {
                var header = this.headers[i];
                var hasMatchingConfig = allowedConfigHeaders.some((c) => c.key === header.key);
                if (!hasMatchingConfig) {
                    allowedConfigHeaders.push({
                        key: header.key,
                        pinned: false,
                        visible: true,
                    });
                }
            }

            return allowedConfigHeaders;
        },

        getViewById() {
            return new Promise((resolve, reject) => {
                var route = `/service/Views?$filter=Id eq ${this.view.id}&$select=Name,Type,Config`;

                this.$api
                    .get(route)
                    .then((response) => {
                        var config = JSON.parse(response.data.value[0].Config);

                        this.view.name = response.data.value[0].Name;
                        this.view.type = response.data.value[0].Type;

                        this.view.filters = config.filters;
                        this.view.icon = config.icon;
                        this.view.display = config.display;
                        this.view.itemsPerPage = config.itemsPerPage;
                        this.view.sort = config.sort;
                        this.view.headers = config.headers;

                        resolve();
                    })

                    .catch((error) => {
                        this.$helpers.showFailNotification('Failed to get view.', { error });
                        this.closeWidget();

                        reject();
                    });
            });
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        updateFilters(filters) {
            this.view.filters = filters;
        },

        updateHeaders(headers) {
            this.view.headers = headers;
        },

        updateIcon(icon) {
            this.view.icon = icon;
        },

        confirmDelete() {
            this.$modal.show(
                AppPopup,
                {
                    type: 'alert-popup',
                    title: `Are you sure?`,
                    content: `<p>This view will get deleted. You can not undo this action.</p>`,
                    actions: {
                        confirm: {
                            label: `Delete`,
                            action: this.deleteView,
                        },
                        cancel: {
                            label: 'Cancel',
                        },
                    },
                },
                {
                    height: this.$helpers.setPopupHeight(),
                    width: this.$helpers.setPopupWidth(),
                },
            );
        },

        deleteView() {
            this.loading = true;

            this.$api
                .delete(`/api/Views/${this.view.id}`)
                .then(() => {
                    EventBus.$emit('view:update');
                    this.$helpers.showSuccessNotification('View deleted.');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('Failed to delete view.', { error });
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        saveView() {
            if (this.view.id) {
                this.updateView();
            } else {
                this.insertView();
            }
        },

        constructConfig() {
            return {
                filters: this.view.filters,
                icon: this.view.icon,
                display: this.view.display,
                itemsPerPage: this.view.itemsPerPage,
                sort: this.view.sort,
                headers: this.view.headers,
            };
        },

        insertView() {
            this.loading = true;

            var request = {
                name: this.view.name,
                type: this.view.type,
                config: JSON.stringify(this.constructConfig()),
            };

            this.$api
                .post(`/api/Views`, request)
                .then(() => {
                    EventBus.$emit('view:update');
                    this.$helpers.showSuccessNotification('View created.');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('Failed to create view.', { error });
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        updateView() {
            this.loading = true;

            var request = {
                name: this.view.name,
                config: JSON.stringify(this.constructConfig()),
            };

            this.$api
                .put(`/api/Views/${this.view.id}`, request)
                .then(() => {
                    EventBus.$emit('view:update');
                    this.$helpers.showSuccessNotification('View updated.');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('Failed to update view.', { error });
                })
                .finally(() => {
                    this.loading = false;
                });
        },
    },
};
</script>

<style>
.board-view-widget .content-wrapper .loading-screen {
    text-align: center;
}

.board-view-widget .app-border-section {
    margin-top: 35px;
}

.board-view-widget .action-container {
    display: flex;
}

.board-view-widget .action-container .delete-btn {
    background-color: #f30070;
    height: 40px;
    border-radius: 20px;
    padding: 0px 45px;
    font-size: 14px;
    line-height: 17px;
    font-weight: 500;
    margin-right: 30px;
}

.board-view-widget .action-container .delete-btn:hover {
    background: #c80768;
}

.board-view-widget .action-container .save-btn {
    height: 40px;
    border-radius: 20px;
    padding: 0px 45px;
    font-size: 14px;
    line-height: 17px;
    font-weight: 500;
}

.board-view-widget .widget-footer .cancel-btn {
    border: none;
    background: transparent;
    color: #473a58;
    padding: 0px 45px;
    -webkit-transition: background-color 500ms ease-out;
    transition: background-color 500ms ease-out;
}

.board-view-widget .widget-footer .cancel-btn:hover {
    background: #e9e9ef;
    border: none;
}

.board-view-widget .app-border-section .content {
    padding: 20px 20px;
}

.board-view-widget .app-border-section .app-tooltip .content {
    padding-left: 0;
}

.board-view-widget .board-view-filters .filter .where-operation,
.board-view-widget .board-view-filters .filter .filter-operation-dropdown,
.board-view-widget .board-view-filters .filter .filter-header-dropdown,
.board-view-widget .board-view-filters .filter .filter-method-dropdown {
    margin-right: 11px;
}

.board-view-widget .board-view-filters .new-filter .placeholder {
    margin-right: 10px;
}
</style>
